/**
 * UI 共通部品
 * - 画面の切替と履歴（進む／戻るでアニメーションの向きを変える）
 * - ボトムシート、アクションシート、確認ダイアログ（window.confirm の置き換え）、トースト
 * - 暗室モード（トークンの差し替え。0.9 秒のフェード、現像画面での自動切替）
 * - 工程レール、ゾーンスケール、アイコン
 * - HTML エスケープ（XSS 対策, CLAUDE.md S3-4）
 */

import { prefs } from './prefs.js';

export const SCREENS = [
  'home', 'select-gear', 'gear', 'gear-edit',
  'viewfinder', 'loading', 'mapping', 'exposure',
  'development', 'reflection', 'library', 'analytics',
  'settings', 'zone-chart', 'learn', 'learn-article', 'ask',
];

const TRANSIENT_SCREENS = new Set(['loading']);
const screenStack = ['home'];
let currentScreen = 'home';
const screenListeners = new Set();

export const ZONE_ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
export const ZONE_NAMES = ['完全な黒', 'ほぼ黒', '質感のある最初の黒', '重要なシャドウ', '暗い中間調', '中間グレー', '明るい中間調', '明るい質感', '質感のある最後の白', 'ほぼ白', '純白'];
export const ZONE_EXAMPLES = ['印画紙の最大濃度', 'わずかな階調', '最も暗い部分の質感', '影の中の岩肌', '日陰の葉、暗い石', '18% グレーカード', '明るい石、晴天の肌', '明るい肌、雪の陰影', '質感のある雪', '質感のない白', '光源、鏡面反射'];

export function zoneToRoman(z) {
  return ZONE_ROMAN[z] ?? String(z);
}

// --------------------------------------------------
// 画面
// --------------------------------------------------
export function getCurrentScreen() { return currentScreen; }

export function onScreenChange(fn) {
  screenListeners.add(fn);
  return () => screenListeners.delete(fn);
}

/**
 * @param {string} name
 * @param {{ back?: boolean, anim?: 'slide'|'fade'|'none' }} [opts]
 */
export function showScreen(name, opts = {}) {
  if (!SCREENS.includes(name)) {
    console.warn('[ui] unknown screen:', name);
    return;
  }
  const prev = currentScreen;
  const anim = opts.anim || (name === 'viewfinder' || name === 'loading' ? 'fade' : 'slide');
  document.querySelectorAll('.screen').forEach((el) => {
    const on = el.id === `screen-${name}`;
    el.classList.toggle('screen--active', on);
    el.classList.remove('screen--in-f', 'screen--in-b', 'screen--fade');
    if (on && anim !== 'none') {
      void el.offsetWidth; // アニメーションを毎回やり直す
      el.classList.add(anim === 'fade' ? 'screen--fade' : (opts.back ? 'screen--in-b' : 'screen--in-f'));
    }
  });
  if (!TRANSIENT_SCREENS.has(name) && screenStack[screenStack.length - 1] !== name) {
    screenStack.push(name);
    if (screenStack.length > 40) screenStack.shift();
  }
  currentScreen = name;
  if (prev !== name) {
    screenListeners.forEach((fn) => {
      try { fn(name, prev); } catch (e) { console.warn('[ui] screen listener:', e); }
    });
  }
  const body = document.querySelector(`#screen-${name} .body`);
  if (body && !opts.keepScroll) body.scrollTop = 0;
}

export function goBack() {
  if (screenStack.length > 1) {
    screenStack.pop();
    const target = screenStack[screenStack.length - 1];
    screenStack.pop();
    showScreen(target, { back: true });
  } else {
    showScreen('home', { back: true });
  }
}

/** 履歴を消して移動（撮り直し・閉じるなど） */
export function resetScreen(name, opts = { back: true }) {
  screenStack.length = 0;
  if (name !== 'home') screenStack.push('home');
  showScreen(name, opts);
}

export function setLoading(message, sub = '') {
  const m = document.getElementById('loading-message');
  const s = document.getElementById('loading-sub');
  if (m) m.textContent = message || '処理しています';
  if (s) s.textContent = sub;
  if (currentScreen !== 'loading') showScreen('loading');
}

// --------------------------------------------------
// アイコン
// --------------------------------------------------
export function icon(name, size = 20, extra = '') {
  return `<svg class="i" width="${size}" height="${size}" aria-hidden="true" ${extra}><use href="#i-${name}"/></svg>`;
}

export function buzz(pattern) {
  try { navigator.vibrate?.(pattern); } catch {}
}

// --------------------------------------------------
// トースト
// --------------------------------------------------
let toastTimer = null;
export function toast(message, durationMs = 2600) {
  const root = document.getElementById('app');
  document.querySelectorAll('.toast').forEach((el) => el.remove());
  const el = document.createElement('div');
  el.className = 'toast';
  el.setAttribute('role', 'status');
  el.textContent = message;
  root.appendChild(el);
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.remove(), durationMs);
}

// --------------------------------------------------
// ボトムシート
//   一度に 1 枚。開き直すと前のシートは閉じる。
// --------------------------------------------------
let activeSheet = null;

/**
 * @param {{ en?: string, title?: string, body?: string|Node, foot?: string|Node,
 *           headExtra?: string, ruled?: boolean, full?: boolean, height?: string,
 *           onClose?: () => void, className?: string }} o
 * @returns {{ el: HTMLElement, body: HTMLElement, close: () => Promise<void> }}
 */
export function openSheet(o = {}) {
  closeSheetNow();
  const app = document.getElementById('app');
  const scrim = document.createElement('div');
  scrim.className = 'scrim';
  const sheet = document.createElement('div');
  sheet.className = `sheet${o.full ? ' sheet--full' : ''}${o.className ? ` ${o.className}` : ''}`;
  if (o.height) sheet.style.height = o.height;
  sheet.setAttribute('role', 'dialog');
  sheet.innerHTML = `
    <span class="sheet__grip"></span>
    <div class="sheet__head${o.ruled === false ? '' : ' sheet__head--rule'}">
      <div class="sheet__titles">
        ${o.en ? `<div class="eyebrow">${escapeHtml(o.en)}</div>` : ''}
        ${o.title ? `<div class="sheet__title">${escapeHtml(o.title)}</div>` : ''}
      </div>
      ${o.headExtra || ''}
      <button class="bar__btn" type="button" data-sheet-close aria-label="閉じる">${icon('close')}</button>
    </div>
    <div class="sheet__body"></div>
  `;
  const body = sheet.querySelector('.sheet__body');
  if (typeof o.body === 'string') body.innerHTML = o.body;
  else if (o.body) body.appendChild(o.body);
  if (o.foot) {
    const foot = document.createElement('div');
    foot.className = 'sheet__foot';
    if (typeof o.foot === 'string') foot.innerHTML = o.foot;
    else foot.appendChild(o.foot);
    sheet.appendChild(foot);
  }
  app.appendChild(scrim);
  app.appendChild(sheet);

  const handle = { el: sheet, body, scrim, onClose: o.onClose, closing: false };
  handle.close = () => closeSheet(handle);
  scrim.addEventListener('click', () => handle.close());
  sheet.querySelector('[data-sheet-close]').addEventListener('click', () => handle.close());
  activeSheet = handle;
  return handle;
}

export function closeSheet(handle = activeSheet) {
  if (!handle || handle.closing) return Promise.resolve();
  handle.closing = true;
  handle.el.classList.add('is-closing');
  handle.scrim.classList.add('is-closing');
  if (activeSheet === handle) activeSheet = null;
  return new Promise((resolve) => {
    setTimeout(() => {
      handle.el.remove();
      handle.scrim.remove();
      try { handle.onClose?.(); } catch (e) { console.warn(e); }
      resolve();
    }, 240);
  });
}

function closeSheetNow() {
  if (!activeSheet) return;
  const h = activeSheet;
  activeSheet = null;
  h.el.remove();
  h.scrim.remove();
  try { h.onClose?.(); } catch {}
}

export function isSheetOpen() { return !!activeSheet; }

/**
 * アクションシート（ライブラリの行の … など）
 * @param {{ title: string, meta?: string, actions: Array<{ label: string, icon?: string, run: () => void }> }} o
 */
export function openActionSheet(o) {
  closeSheetNow();
  const app = document.getElementById('app');
  const scrim = document.createElement('div');
  scrim.className = 'scrim';
  const el = document.createElement('div');
  el.className = 'action-sheet';
  el.innerHTML = `
    <div class="action-sheet__card">
      <div class="action-sheet__head">
        <div style="font:500 15px/1.3 var(--font-jp-sans)">${escapeHtml(o.title)}</div>
        ${o.meta ? `<div class="en sub" style="font-size:12px;margin-top:3px">${escapeHtml(o.meta)}</div>` : ''}
      </div>
      ${o.actions.map((a, i) => `<button class="action-sheet__btn" type="button" data-act="${i}">${a.icon ? icon(a.icon) : ''}${escapeHtml(a.label)}</button>`).join('')}
    </div>
    <button class="action-sheet__cancel" type="button" data-cancel>キャンセル</button>
  `;
  app.appendChild(scrim);
  app.appendChild(el);
  const handle = { el, scrim, closing: false };
  handle.close = () => closeSheet(handle);
  scrim.addEventListener('click', () => handle.close());
  el.querySelector('[data-cancel]').addEventListener('click', () => handle.close());
  el.querySelectorAll('[data-act]').forEach((b) => {
    b.addEventListener('click', async () => {
      await handle.close();
      o.actions[Number(b.dataset.act)].run();
    });
  });
  activeSheet = handle;
  return handle;
}

// --------------------------------------------------
// 確認ダイアログ
// --------------------------------------------------
/**
 * @param {{ title: string, body?: string, ok?: string }} o
 * @returns {Promise<boolean>}
 */
export function confirmDialog(o) {
  return new Promise((resolve) => {
    const app = document.getElementById('app');
    const scrim = document.createElement('div');
    scrim.className = 'confirm-scrim';
    const el = document.createElement('div');
    el.className = 'confirm';
    el.setAttribute('role', 'alertdialog');
    el.innerHTML = `
      <div class="eyebrow">confirm</div>
      <div class="confirm__title">${escapeHtml(o.title)}</div>
      ${o.body ? `<p class="confirm__body">${escapeHtml(o.body)}</p>` : ''}
      <div class="confirm__actions">
        <button class="confirm__ok" type="button">${escapeHtml(o.ok || '実行')}</button>
        <button class="confirm__no" type="button">キャンセル</button>
      </div>
    `;
    const done = (v) => { scrim.remove(); el.remove(); resolve(v); };
    scrim.addEventListener('click', () => done(false));
    el.querySelector('.confirm__no').addEventListener('click', () => done(false));
    el.querySelector('.confirm__ok').addEventListener('click', () => { buzz(10); done(true); });
    app.appendChild(scrim);
    app.appendChild(el);
  });
}

// --------------------------------------------------
// 暗室モード
// --------------------------------------------------
let autoDark = false;

export function isDarkroomMode() {
  return document.body.classList.contains('darkroom');
}
export function isAutoDarkroom() { return autoDark && isDarkroomMode(); }

/**
 * @param {boolean} on
 * @param {{ auto?: boolean, fade?: boolean, persist?: boolean }} [opts]
 *   auto: 現像画面の自動切替（離れると戻す。記憶しない）
 */
export function setDarkroomMode(on, opts = {}) {
  const was = isDarkroomMode();
  autoDark = !!(on && opts.auto);
  if (was !== !!on && opts.fade !== false) {
    const fx = document.createElement('div');
    fx.className = 'fx-lights';
    document.getElementById('app')?.appendChild(fx);
    setTimeout(() => fx.remove(), 1250);
  }
  document.body.classList.toggle('darkroom', !!on);
  applyDarkLevel();
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', on ? '#0a0000' : '#ffffff');
  if (opts.persist) prefs.darkroom = !!on;
  updateDarkChip();
  darkListeners.forEach((fn) => { try { fn(!!on); } catch {} });
}

/** 26. 暗室モードの明るさ（標準／暗め／最も暗い）。画面全体の明るさを落とす */
export function applyDarkLevel() {
  document.body.dataset.darkLevel = prefs.darkLevel;
}

const darkListeners = new Set();
export function onDarkroomChange(fn) {
  darkListeners.add(fn);
  return () => darkListeners.delete(fn);
}

/** 現像・撮影以外で暗室のとき、上部に「暗室モード中 · タップで解除」 */
export function updateDarkChip() {
  const app = document.getElementById('app');
  if (!app) return;
  let chip = document.getElementById('dark-chip');
  const show = isDarkroomMode() && !['development', 'viewfinder'].includes(currentScreen);
  if (!show) { chip?.remove(); return; }
  if (chip) return;
  chip = document.createElement('button');
  chip.id = 'dark-chip';
  chip.type = 'button';
  chip.className = 'dark-chip';
  chip.innerHTML = `${icon('moon-fill', 13)}暗室モード中 · タップで解除`;
  chip.addEventListener('click', () => setDarkroomMode(false, { persist: true }));
  app.appendChild(chip);
}
onScreenChange(() => updateDarkChip());

// --------------------------------------------------
// 工程レール（01 撮影 → 05 振り返り）
// --------------------------------------------------
const RAIL = [['01', '撮影'], ['02', '測光'], ['03', '露出'], ['04', '現像'], ['05', '振り返り']];

/**
 * @param {number} cur 現在の工程（0〜4）
 * @param {Record<number, () => void>} links タップで移動できる工程
 */
export function renderRail(container, cur, links = {}) {
  container.className = 'rail';
  container.innerHTML = RAIL.map(([n, t], i) => {
    const cls = ['rail__step'];
    if (i < cur) cls.push('is-done');
    if (i === cur) cls.push('is-cur');
    if (links[i]) cls.push('is-link');
    return `<button type="button" class="${cls.join(' ')}" data-rail="${i}" ${links[i] ? '' : 'tabindex="-1"'}><span class="rail__fill"></span><span class="rail__n">${n}</span><span class="rail__t">${t}</span></button>`;
  }).join('');
  container.querySelectorAll('[data-rail]').forEach((b) => {
    const fn = links[Number(b.dataset.rail)];
    if (fn) b.addEventListener('click', fn);
  });
}

// --------------------------------------------------
// ゾーン
// --------------------------------------------------
export function zoneCellsHtml(from = 0, to = 10, offClass = '') {
  let out = '';
  for (let z = 0; z <= 10; z++) {
    out += `<span class="${z >= from && z <= to ? `zc-${z}` : offClass}"></span>`;
  }
  return out;
}

/**
 * ゾーンスケール（11 マス）。タップでもなぞっても選べる。
 * @param {HTMLElement} container
 * @param {number} zone
 * @param {(z: number) => void} onChange
 * @param {{ labels?: HTMLElement }} [opts] ローマ数字の目盛り（選択中を強調）
 */
export function buildZoneScale(container, zone, onChange, opts = {}) {
  container.classList.add('zscale');
  container.innerHTML = `${zoneCellsHtml()}<span class="zscale__sel"></span>`;
  const sel = container.querySelector('.zscale__sel');
  let cur = zone;
  const paint = () => {
    sel.style.left = `${(cur * 100) / 11}%`;
    if (opts.labels) {
      opts.labels.className = 'zlabels';
      opts.labels.innerHTML = ZONE_ROMAN.map((r, i) => `<span class="${i === cur ? 'is-on' : ''}">${r}</span>`).join('');
    }
  };
  paint();
  const at = (e) => {
    const r = container.getBoundingClientRect();
    return Math.max(0, Math.min(10, Math.floor(((e.clientX - r.left) / r.width) * 11)));
  };
  let dragging = false;
  const set = (z) => {
    if (z === cur) return;
    cur = z;
    paint();
    buzz(4);
    onChange?.(z);
  };
  container.onpointerdown = (e) => {
    dragging = true;
    try { container.setPointerCapture(e.pointerId); } catch {}
    const z = at(e);
    if (z === cur) onChange?.(z);
    set(z);
  };
  container.onpointermove = (e) => { if (dragging) set(at(e)); };
  container.onpointerup = () => { dragging = false; };
  container.onpointercancel = () => { dragging = false; };
  return { set: (z) => { cur = z; paint(); }, get: () => cur };
}

// --------------------------------------------------
// 長押しで連続（± ボタン）
// --------------------------------------------------
export function holdRepeat(el, fn) {
  let t = null;
  let iv = null;
  const stop = () => { clearTimeout(t); clearInterval(iv); t = null; iv = null; };
  el.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    fn();
    stop();
    t = setTimeout(() => { iv = setInterval(fn, 80); }, 350);
  });
  ['pointerup', 'pointerleave', 'pointercancel'].forEach((ev) => el.addEventListener(ev, stop));
  el.addEventListener('click', (e) => e.preventDefault());
}

// --------------------------------------------------
// HTML エスケープ（XSS 対策, CLAUDE.md S3-4）
// --------------------------------------------------
export function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// --------------------------------------------------
// 日付
// --------------------------------------------------
export function formatDate(ts) {
  const d = new Date(ts);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}
export function formatDateTime(ts) {
  const d = new Date(ts);
  return `${formatDate(ts)} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}
export function formatClock(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}
export function formatRelative(ts) {
  const day = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  if (day < 1) return '今日';
  if (day < 7) return `${day}日前`;
  if (day < 30) return `${Math.floor(day / 7)}週間前`;
  if (day < 365) return `${Math.floor(day / 30)}か月前`;
  return `${Math.floor(day / 365)}年前`;
}

// --------------------------------------------------
// Blob → object URL（画面ごとにまとめて解放する）
// --------------------------------------------------
const urlBuckets = new Map();
export function blobUrl(bucket, blob) {
  if (!blob) return '';
  const url = URL.createObjectURL(blob);
  if (!urlBuckets.has(bucket)) urlBuckets.set(bucket, []);
  urlBuckets.get(bucket).push(url);
  return url;
}
export function releaseUrls(bucket) {
  (urlBuckets.get(bucket) || []).forEach((u) => { try { URL.revokeObjectURL(u); } catch {} });
  urlBuckets.delete(bucket);
}

/** 画面の頭（safe area + バー）。left / right は HTML */
export function barHtml(left = '', right = '', center = '') {
  return `<div class="safe-top"></div><div class="bar">${left || '<span class="bar__btn"></span>'}${center}${right || '<span class="bar__btn"></span>'}</div>`;
}
export function backBtn(attr = 'data-back', label = '戻る') {
  return `<button class="bar__btn" type="button" ${attr} aria-label="${escapeHtml(label)}">${icon('back', 22)}</button>`;
}

// --------------------------------------------------
// 27. 関連する教材の章へのリンク（クリックは main.js でまとめて受ける）
// --------------------------------------------------
export function chapterLink(articleId, label) {
  return `<button type="button" class="chapter-link" data-chapter="${escapeHtml(articleId)}">${icon('learn', 13)}<span>${escapeHtml(label)}</span>${icon('chev', 12)}</button>`;
}
